import { useEffect, useState, useCallback } from "react";
import { adminFetch } from "./adminUtils";
import { toast } from "sonner";
import {
  Activity, AlertCircle, CheckCircle2, RefreshCw, Loader2, Zap, Timer,
  TrendingUp, Shield, Play, Pause, Image,
} from "lucide-react";

/**
 * AI Providers health dashboard — shows circuit breaker states
 * and last-24h generation stats (success rate, provider mix, latency).
 */
export default function AdminAIProviders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await adminFetch("/api/admin/ai-providers/status");
      setData(d);
    } catch (e) {
      toast.error(e?.message || "Error al cargar estado de proveedores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(id);
  }, [load]);

  const handleReset = async (provider) => {
    setResetting(provider);
    try {
      await adminFetch("/api/admin/ai-providers/reset-breaker", {
        method: "POST",
        body: JSON.stringify({ provider }),
      });
      toast.success(`Breaker '${provider}' reseteado`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Error al resetear");
    } finally {
      setResetting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const breakers = data?.breakers || [];
  const stats = data?.stats_24h || {};
  const providerCounts = stats.provider_counts || {};
  const totalGenerated = Object.values(providerCounts).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="p-8 space-y-6 max-w-6xl" data-testid="admin-ai-providers-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan-400" />
            Estado de proveedores IA
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Circuit breakers, fallback automático y estadísticas de generación de imágenes.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-sm transition"
          data-testid="ai-providers-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* 24h stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Image className="w-4 h-4" />}
          label="Imágenes 24h"
          value={stats.total_image_jobs || 0}
          color="cyan"
        />
        <StatCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Tasa de éxito"
          value={`${stats.success_rate || 0}%`}
          color={stats.success_rate >= 95 ? "emerald" : stats.success_rate >= 80 ? "amber" : "red"}
        />
        <StatCard
          icon={<Timer className="w-4 h-4" />}
          label="Latencia media"
          value={`${stats.avg_latency_seconds || 0}s`}
          color="violet"
        />
        <StatCard
          icon={<AlertCircle className="w-4 h-4" />}
          label="Fallidos"
          value={stats.failed || 0}
          color={(stats.failed || 0) > 0 ? "red" : "slate"}
        />
      </div>

      {/* Provider mix */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-white text-sm font-semibold flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          Mix de proveedores — últimas 24h
        </h2>
        {Object.keys(providerCounts).length === 0 ? (
          <p className="text-muted-foreground text-xs">Aún no hay datos.</p>
        ) : (
          <div className="space-y-2">
            {Object.entries(providerCounts).map(([provider, count]) => {
              const pct = Math.round((count / totalGenerated) * 100);
              const isFallback = provider.includes("openai");
              return (
                <div key={provider} data-testid={`provider-bar-${provider}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={isFallback ? "text-amber-400" : "text-emerald-400"}>
                      {provider === "gemini" ? "Gemini (primario)"
                        : provider === "openai_gpt_image_1" ? "OpenAI gpt-image-1 (fallback)"
                        : provider}
                    </span>
                    <span className="text-slate-400 font-mono">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${isFallback ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Breakers */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-white text-sm font-semibold flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-orange-400" />
          Circuit breakers
        </h2>
        {breakers.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Todos los proveedores operativos — sin fallos registrados.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {breakers.map((b) => (
              <BreakerCard
                key={b.provider}
                breaker={b}
                onReset={handleReset}
                resetting={resetting === b.provider}
              />
            ))}
          </div>
        )}
      </div>

      <p className="text-muted-foreground text-xs">
        Auto-refresh cada 15 segundos · Última actualización: {new Date(data?.timestamp || Date.now()).toLocaleTimeString()}
      </p>
    </div>
  );
}

function StatCard({ icon, label, value, color = "slate" }) {
  const colorMap = {
    cyan: "border-cyan-500/20 text-cyan-400",
    emerald: "border-emerald-500/20 text-emerald-400",
    violet: "border-violet-500/20 text-violet-400",
    amber: "border-amber-500/20 text-amber-400",
    red: "border-red-500/20 text-red-400",
    slate: "border-slate-700 text-slate-400",
  };
  return (
    <div className={`bg-slate-900 border ${colorMap[color]} rounded-xl p-4`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <p className="text-white text-2xl font-bold mt-1.5">{value}</p>
    </div>
  );
}

function BreakerCard({ breaker, onReset, resetting }) {
  const isOpen = breaker.is_open;
  return (
    <div
      className={`border rounded-lg p-4 ${
        isOpen ? "bg-red-500/5 border-red-500/30" : "bg-emerald-500/5 border-emerald-500/20"
      }`}
      data-testid={`breaker-${breaker.provider}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isOpen ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"
          }`}>
            {isOpen ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium text-sm">{breaker.provider}</p>
            <div className="flex items-center gap-2 mt-0.5 text-[11px]">
              <span className={isOpen ? "text-red-400" : "text-emerald-400"}>
                {isOpen ? "ABIERTO" : "CERRADO"}
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{breaker.failure_count} fallos recientes</span>
              {isOpen && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-red-400">
                    Reabrirá en {Math.floor(breaker.seconds_remaining / 60)}:{String(breaker.seconds_remaining % 60).padStart(2, "0")}
                  </span>
                </>
              )}
            </div>
            {breaker.last_failure_reason && (
              <p className="text-[10px] text-muted-foreground mt-1 truncate max-w-md" title={breaker.last_failure_reason}>
                Último error: {breaker.last_failure_reason.slice(0, 90)}
              </p>
            )}
          </div>
        </div>
        {isOpen && (
          <button
            onClick={() => onReset(breaker.provider)}
            disabled={resetting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500/10 text-orange-400 border border-orange-500/30 hover:bg-orange-500/20 text-xs font-medium transition"
            data-testid={`breaker-reset-${breaker.provider}-btn`}
          >
            {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Reabrir
          </button>
        )}
      </div>
    </div>
  );
}
